import { TProject, TUser } from '@/types/models.ts';
import { AppDialog } from '@/components/app/app-dialog.tsx';
import { AppForm } from '@/components/form/form.tsx';
import { InputField } from '@/components/form/input-field.tsx';
import { ComboboxField } from '@/components/form/combobox-field.tsx';
import { countryOpts } from '@/lib/locales.ts';
import { useAuth } from '@/stores/auth.store.ts';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { d } from '@/lib/form.ts';
import { Button } from '@/components/ui/button.tsx';
import { useProjectSaveMutation } from '@/app/account/api/queries.ts';

const formSchema = z.object({
  id: z.coerce.number().optional(),
  name: z.string().min(1, 'Project name is required'),
  client: z.string().optional(),
  shipping_address: z.object({
    address: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    country: z.string().optional(),
  }),
});

export type FormType = z.input<typeof formSchema>;

type Props = {
  project?: TProject;
  onClose: () => void;
};

function getTitle(project?: TProject) {
  return project ? `Update Project: ${project.name}` : 'Create New Project';
}

function getCountry(user: TUser, project?: TProject) {
  return project?.shipping_address?.country ? project.shipping_address.country : user.site_id == 2 ? 'GB' : 'US';
}

export function ProjectEdit({ project, onClose }: Props) {
  const { user } = useAuth();
  const saveProject = useProjectSaveMutation();
  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      id: d.num(project?.id),
      name: d.str(project?.name),
      client: d.str(project?.client),
      shipping_address: {
        address: d.str(project?.shipping_address?.address),
        address2: d.str(project?.shipping_address?.address2),
        city: d.str(project?.shipping_address?.city),
        state: d.str(project?.shipping_address?.state),
        zip: d.str(project?.shipping_address?.zip),
        country: d.str(getCountry(user, project)),
      },
    },
  });

  const onSubmit = (data: FormType) => {
    if (project?.id) {
      data['id'] = project.id;
    }
    saveProject.mutateAsync(data).then(() => {
      onClose();
    });
  };

  return (
    <AppDialog title={getTitle(project)} onClose={onClose}>
      <AppForm form={form} onSubmit={onSubmit} className="w-full space-y-4 p-2 mx-auto mb-8">
        <div className="dialog-body space-y-4">
          <InputField label="Name" name="name" autoFocus />
          <h3 className="text-center mb-1">Client Details</h3>
          <InputField label="Client Name" name="client" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InputField label="Address" name="shipping_address.address" />
            <InputField label="Address 2" name="shipping_address.address2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <InputField label="City/Town" name="shipping_address.city" />
            <InputField label="State/Region" name="shipping_address.state" />
            <InputField label="Zip/Postal Code" name="shipping_address.zip" />
          </div>
          <div className="grid grid-cols-1 gap-3">
            <ComboboxField label="Country" name="shipping_address.country" items={countryOpts} />
          </div>
        </div>
        <div className="dialog-footer">
          <Button disabled={!form.formState.isDirty}>Save</Button>
        </div>
      </AppForm>
    </AppDialog>
  );
}
